import { useState } from 'react'
import { Popover } from '@headlessui/react'
import { useApolloClient } from '@apollo/client'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import {
  Button,
  Container,
  Logo,
  NavLink,
} from './index'
import { USER_LOGOUT_MUTATION } from '../../graphql/mutations'
import { useAuth } from '../../contexts/AuthContext/AuthContext'
import { ROOT_PATH } from '../../config/rootPath'

function MobileNavLink({
  to,
  children,
}: {
  to: string
  children: React.ReactNode
}) {
  return (
    <Link to={to} className="block w-full p-2">
      {children}
    </Link>
  )
}

function MobileNavIcon({ open }: { open: boolean | undefined }) {
  return (
    <svg
      aria-hidden="true"
      className="h-3.5 w-3.5 overflow-visible stroke-slate-700"
      fill="none"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <path
        d="M0 1H14M0 7H14M0 13H14"
        className={clsx(
          'origin-center transition',
          open && 'scale-90 opacity-0',
        )}
      />
      <path
        d="M2 2L12 12M12 2L2 12"
        className={clsx(
          'origin-center transition',
          !open && 'scale-90 opacity-0',
        )}
      />
    </svg>
  )
}

function MobileNavigation({
 onLogoutClick, isAuthenticated 
}: { onLogoutClick: () => void, isAuthenticated: boolean }) {
  return (
    <Popover>
      {({ open }) => (
        <>
          <Popover.Button
            className="relative z-10 flex h-8 w-8 items-center justify-center focus:not-data-focus:outline-hidden"
            aria-label="Toggle Navigation"
          >
            <MobileNavIcon open={open} />
          </Popover.Button>
          <Popover.Overlay
            className="fixed inset-0 bg-slate-300/50"
          />
          <Popover.Panel
            className="absolute inset-x-0 top-full mt-4 flex origin-top flex-col rounded-2xl bg-white p-4 text-lg tracking-tight text-slate-900 shadow-xl ring-1 ring-slate-900/5"
          >
            {isAuthenticated ? (
              <>
                <MobileNavLink to="/dashboard">Dashboard</MobileNavLink>
                <MobileNavLink to="/company-info">Company Info</MobileNavLink>
                <MobileNavLink to="/projects">Projects</MobileNavLink>
                <MobileNavLink to="/employees">Employees</MobileNavLink>
                <hr className="m-2 border-slate-300/40" />
                <button onClick={onLogoutClick} className="block w-full p-2 text-left cursor-pointer">
                  Logout
                </button>
              </>
            ) : (
              <>
                <a
                  href="#features"
                  onClick={(e) => {
                    e.preventDefault()
                    const element = document.getElementById('features')
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' })
                    }
                  }}
                  className="block w-full p-2"
                >
                  Features
                </a>
                <a
                  href="#pricing"
                  onClick={(e) => {
                    e.preventDefault()
                    const element = document.getElementById('pricing')
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' })
                    }
                  }}
                  className="block w-full p-2"
                >
                  Pricing
                </a>
                <a
                  href="#faqs"
                  onClick={(e) => {
                    e.preventDefault()
                    const element = document.getElementById('faqs')
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' })
                    }
                  }}
                  className="block w-full p-2"
                >
                  FAQs
                </a>
                <hr className="m-2 border-slate-300/40" />
                <MobileNavLink to="/login">Sign in</MobileNavLink>
                <MobileNavLink to="/register">Create account</MobileNavLink>
              </>
            )}
          </Popover.Panel>
        </>
      )}
    </Popover>
  )
}

export function SalientHeader() {
  const [logoutError, setLogoutError] = useState<string | null>(null)
  const client = useApolloClient()
  const {
 isAuthenticated, logout 
} = useAuth()

  const handleLogout = async () => {
    setLogoutError(null)
    const result = await client.mutate({
      mutation: USER_LOGOUT_MUTATION,
      variables: {input: {}}
    })

    if (result.data.userLogout.success) {
      logout()
    } else {
      setLogoutError(result.data.userLogout.errors?.[0] || 'Failed to logout')
    }
  }

  return (
    <header className="py-10">
      <Container>
        <nav className="relative z-50 flex justify-between">
          <div className="flex items-center md:gap-x-12">
            <Link to={ROOT_PATH} aria-label="Home">
              <Logo className="h-10 w-auto" variant="image" showText={true} size="lg" />
            </Link>
            <div className="hidden md:flex md:gap-x-6">
              {isAuthenticated ? (
                <>
                  <NavLink to="/dashboard">Dashboard</NavLink>
                  <NavLink to="/company-info">Company Info</NavLink>
                  <NavLink to="/projects">Projects</NavLink>
                  <NavLink to="/employees">Employees</NavLink>
                </>
              ) : (
                <>
                  <a
                    href="#features"
                    onClick={(e) => {
                      e.preventDefault()
                      const element = document.getElementById('features')
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' })
                      }
                    }}
                  >
                    Features
                  </a>
                  <a
                    href="#pricing"
                    onClick={(e) => {
                      e.preventDefault()
                      const element = document.getElementById('pricing')
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' })
                      }
                    }}
                  >
                    Pricing
                  </a>
                  <a
                    href="#faqs"
                    onClick={(e) => {
                      e.preventDefault()
                      const element = document.getElementById('faqs')
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' })
                      }
                    }}
                  >
                    FAQs
                  </a>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-x-5 md:gap-x-8">
            {isAuthenticated ? (
              <div className="flex items-center gap-x-5 md:gap-x-8">
                {logoutError && (
                  <div className="text-sm text-red-600">{logoutError}</div>
                )}
                <Button onClick={handleLogout} variant="outline" color="slate">
                  Logout
                </Button>
              </div>
            ) : (
              <>
                <div className="hidden md:block">
                  <NavLink to="/login">Sign in</NavLink>
                </div>
                <Link to="/register">
                  <Button color="blue">
                    <span>
                      Create account <span className="hidden lg:inline">today</span>
                    </span>
                  </Button>
                </Link>
              </>
            )}
            <div className="-mr-1 md:hidden">
              <MobileNavigation onLogoutClick={handleLogout} isAuthenticated={isAuthenticated} />
            </div>
          </div>
        </nav>
      </Container>
    </header>
  )
} 